import {
  letterTemplateDocumentHasMeaningfulContent,
} from './letterTemplateBilingualState.js';
import { letterTemplateDocumentsEqual } from './letterTemplateDocument.js';
import { projectCallableLetterTemplateTranslationMetadata } from '../../../services/admin/letterTemplateTranslationMetadata.js';

/**
 * @typedef {import('../../../services/admin/letterTemplateTranslationMetadata.js').LetterTemplateTranslationMetadata} LetterTemplateTranslationMetadata
 */

/**
 * @typedef {{
 *   frenchSnapshot: object,
 *   englishSnapshot: object | null,
 *   bilingual: boolean,
 *   editorSessionKey: string,
 * }} TranslationRequestGuard
 */

/**
 * @param {{
 *   frenchSnapshot: object,
 *   englishSnapshot?: object | null,
 *   bilingual: boolean,
 *   editorSessionKey: string,
 * }} params
 * @returns {TranslationRequestGuard}
 */
export function createTranslationRequestGuard({
  frenchSnapshot,
  englishSnapshot = null,
  bilingual,
  editorSessionKey,
}) {
  return {
    frenchSnapshot,
    englishSnapshot: englishSnapshot || null,
    bilingual: Boolean(bilingual),
    editorSessionKey: String(editorSessionKey ?? ''),
  };
}

/**
 * @param {object | null | undefined} frenchDocument
 * @param {LetterTemplateTranslationMetadata | null | undefined} metadata
 * @param {object | null | undefined} sourceSnapshot
 */
export function isTranslationStale(frenchDocument, metadata, sourceSnapshot) {
  if (!metadata) return false;
  if (!sourceSnapshot) return true;
  return !letterTemplateDocumentsEqual(frenchDocument, sourceSnapshot);
}

/**
 * @param {LetterTemplateTranslationMetadata | null | undefined} metadata
 * @param {object | null | undefined} frenchDocument
 * @param {object | null | undefined} sourceSnapshot
 */
export function shouldBlockSaveForStaleTranslation(metadata, frenchDocument, sourceSnapshot) {
  if (!metadata) return false;
  return isTranslationStale(frenchDocument, metadata, sourceSnapshot);
}

/**
 * @param {TranslationRequestGuard} guard
 * @param {{
 *   frenchDocument: object,
 *   englishDocument?: object | null,
 *   bilingual: boolean,
 *   editorSessionKey: string,
 *   response: { document?: object, sourceDocumentHash?: string, model?: string },
 * }} current
 * @returns {{ ok: true, englishDocument: object, metadata: LetterTemplateTranslationMetadata, sourceSnapshot: object, lastTranslatedEnglishSnapshot: object } | { ok: false, reason: string }}
 */
export function validateTranslationResponseAgainstGuard(guard, current) {
  if (!guard) return { ok: false, reason: 'race' };
  if (guard.editorSessionKey !== current.editorSessionKey) {
    return { ok: false, reason: 'session_changed' };
  }
  if (!current.bilingual) {
    return { ok: false, reason: 'bilingual_disabled' };
  }
  if (!letterTemplateDocumentsEqual(guard.frenchSnapshot, current.frenchDocument)) {
    return { ok: false, reason: 'french_changed' };
  }
  const guardEn = guard.englishSnapshot;
  const currentEn = current.englishDocument || null;
  if (!letterTemplateDocumentsEqual(guardEn, currentEn)) {
    return { ok: false, reason: 'english_changed' };
  }

  const englishDocument = current.response?.document;
  if (!englishDocument || englishDocument.type !== 'doc') {
    return { ok: false, reason: 'invalid_response' };
  }

  return {
    ok: true,
    englishDocument,
    metadata: {
      sourceLanguage: 'fr',
      targetLanguage: 'en',
      sourceDocumentHash: String(current.response.sourceDocumentHash || ''),
      model: String(current.response.model || ''),
    },
    sourceSnapshot: guard.frenchSnapshot,
    lastTranslatedEnglishSnapshot: englishDocument,
  };
}

/**
 * @deprecated use validateTranslationResponseAgainstGuard
 */
export function resolveTranslationResponse({
  requestFrenchSnapshot,
  currentFrenchDocument,
  response,
}) {
  return validateTranslationResponseAgainstGuard(
    createTranslationRequestGuard({
      frenchSnapshot: requestFrenchSnapshot,
      englishSnapshot: null,
      bilingual: true,
      editorSessionKey: '',
    }),
    {
      frenchDocument: currentFrenchDocument,
      englishDocument: null,
      bilingual: true,
      editorSessionKey: '',
      response,
    },
  );
}

/**
 * @param {object | null | undefined} loadedFrenchDocument
 * @param {LetterTemplateTranslationMetadata | null | undefined} translation
 * @param {object | null | undefined} loadedEnglishDocument
 */
export function initializeTranslationSession(loadedFrenchDocument, translation, loadedEnglishDocument) {
  if (!translation) {
    return {
      metadata: null,
      sourceSnapshot: null,
      lastTranslatedEnglishSnapshot: null,
      stale: false,
    };
  }
  return {
    metadata: projectCallableLetterTemplateTranslationMetadata(translation),
    sourceSnapshot: loadedFrenchDocument,
    lastTranslatedEnglishSnapshot: loadedEnglishDocument || null,
    stale: false,
  };
}

/**
 * @param {object | null | undefined} englishDocument
 * @param {object | null | undefined} lastTranslatedEnglishSnapshot
 */
export function hasManualEnglishEdits(englishDocument, lastTranslatedEnglishSnapshot) {
  if (!lastTranslatedEnglishSnapshot) return false;
  return !letterTemplateDocumentsEqual(englishDocument, lastTranslatedEnglishSnapshot);
}

/**
 * Confirm before Vertex replaces any meaningful English draft.
 * @param {object | null | undefined} englishDocument
 */
export function shouldConfirmBeforeReplaceEnglish(englishDocument) {
  return letterTemplateDocumentHasMeaningfulContent(englishDocument);
}

/**
 * @param {boolean} hasTranslationMetadata
 */
export function usesRetranslateButtonLabel(hasTranslationMetadata) {
  return Boolean(hasTranslationMetadata);
}

/**
 * Reject in-flight translation results after shell unmount or superseded request.
 * @param {{ componentAlive: boolean, requestId: number, activeRequestId: number }} params
 */
export function shouldApplyInFlightTranslationResult({ componentAlive, requestId, activeRequestId }) {
  if (!componentAlive) return false;
  if (requestId !== activeRequestId) return false;
  return true;
}
